import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArchiveIcon, X, FileText } from 'lucide-react';

const ArchiveInvoiceConfirmationModal = ({ isOpen, onClose, onConfirm, invoice, isArchive = true }) => {
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await onConfirm(invoice._id, { notes });
      onClose();
    } catch (error) {
      console.error(`Error ${isArchive ? 'archiving' : 'unarchiving'} invoice:`, error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 overflow-y-auto bg-grayteck-900 bg-opacity-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="relative bg-white dark:bg-darkbg-800 rounded-lg shadow-xl max-w-md w-full p-4 sm:p-6"
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-grayteck-500 hover:text-grayteck-700 dark:text-grayteck-400 dark:hover:text-grayteck-200"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Modal header */}
          <div className="flex items-center mb-4">
            <div className="mr-3 flex-shrink-0 flex items-center justify-center h-10 w-10 rounded-full bg-purple-100 dark:bg-purple-900/30">
              <ArchiveIcon className="h-6 w-6 text-purple-600 dark:text-purple-400" />
            </div>
            <h3 className="text-lg font-medium text-grayteck-900 dark:text-grayteck-100">
              {isArchive ? 'Archive Invoice' : 'Unarchive Invoice'}
            </h3>
          </div>

          {/* Modal content */}
          <div className="space-y-4">
            <p className="text-sm text-grayteck-600 dark:text-grayteck-400">
              {isArchive 
                ? `You are about to archive invoice #${invoice.invoice_number}. This will mark the invoice as canceled.` 
                : `You are about to unarchive invoice #${invoice.invoice_number}. This will restore the invoice to its previous status.`}
            </p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Notes - Optional */}
              <div>
                <label 
                  htmlFor="archive-notes" 
                  className="block text-sm font-medium text-grayteck-700 dark:text-grayteck-300 mb-1"
                >
                  Notes <span className="text-xs text-grayteck-500 dark:text-grayteck-400">(optional)</span>
                </label>
                <div className="relative rounded-md shadow-sm">
                  <div className="absolute top-3 left-3 pointer-events-none">
                    <FileText className="h-5 w-5 text-grayteck-400" />
                  </div>
                  <textarea
                    id="archive-notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows="3"
                    placeholder={isArchive ? "Reason for archiving..." : "Reason for unarchiving..."}
                    className="focus:ring-grayteck-500 focus:border-grayteck-500 block w-full pl-10 sm:text-sm 
                             border-grayteck-300 dark:border-grayteck-600 rounded-md bg-white dark:bg-darkbg-700 
                             text-grayteck-900 dark:text-grayteck-100 py-2"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col-reverse sm:flex-row justify-end gap-2 sm:space-x-3 pt-4">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 bg-grayteck-100 dark:bg-darkbg-700 text-grayteck-700 dark:text-grayteck-300 
                           rounded-md hover:bg-grayteck-200 dark:hover:bg-darkbg-600 focus:outline-none"
                >
                  Cancel
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  disabled={isSubmitting}
                  className={`px-4 py-2 ${isArchive ? 'bg-purple-600 hover:bg-purple-700' : 'bg-blue-600 hover:bg-blue-700'} text-white rounded-md focus:outline-none
                            flex items-center justify-center min-w-[100px] ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
                >
                  {isSubmitting ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  ) : (
                    isArchive ? 'Archive Invoice' : 'Unarchive Invoice'
                  )}
                </motion.button>
              </div>
            </form>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ArchiveInvoiceConfirmationModal;